import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Lock, Globe } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const GitHubOrgRepoDropdown = ({ onSelect, githubToken }) => {
  // Org state
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  // Repo dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [repos, setRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);
  
  const dropdownRef = useRef(null);
  const searchTimeout = useRef(null);

  // Load orgs when component mounts or token changes
  useEffect(() => {
    const fetchOrgs = async () => {
      // Don't attempt fetch if there's no backend token and no user token
      // We will let the backend decide, but if the user is typing, we want to fetch
      setLoadingOrgs(true);
      try {
        const token = sessionStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        if (githubToken) headers['x-github-token'] = githubToken;
        const response = await fetch(`${BASE_URL}/api/github/orgs`, { headers });
        if (response.ok) {
          const data = await response.json();
          setOrgs(data);
        }
      } catch (error) {
        console.error('Error fetching orgs:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };
    
    // Only fetch if we have a token (or we're relying on the backend default)
    if (githubToken && githubToken.length > 10) {
      const timeout = setTimeout(() => fetchOrgs(), 500); // debounce typing
      return () => clearTimeout(timeout);
    } else if (!githubToken) {
      fetchOrgs();
    }
  }, [githubToken]);

  // Handle outside click for repo dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch repos when org changes, search query changes, or dropdown opens
  useEffect(() => {
    if (!selectedOrg || !isOpen) return;

    const fetchRepos = async () => {
      setLoadingRepos(true);
      try {
        const url = `${BASE_URL}/api/github/orgs/${encodeURIComponent(selectedOrg)}/repos${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`;
        const token = sessionStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        if (githubToken) headers['x-github-token'] = githubToken;
        
        const response = await fetch(url, { headers });
        if (response.ok) {
          const data = await response.json();
          setRepos(data);
        } else {
          setRepos([]);
        }
      } catch (error) {
        console.error('Error fetching repos:', error);
        setRepos([]);
      } finally {
        setLoadingRepos(false);
      }
    };

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    searchTimeout.current = setTimeout(() => {
      fetchRepos();
    }, 250);

    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, isOpen, selectedOrg, githubToken]);

  const handleOrgChange = (e) => {
    setSelectedOrg(e.target.value);
    setSelectedRepo(null);
    setSearchQuery('');
    setRepos([]);
    if (onSelect) onSelect(null);
  };

  const handleSelectRepo = (repo) => {
    setSelectedRepo(repo);
    setIsOpen(false);
    if (onSelect) onSelect(repo);
  };

  return (
    <div className="w-full space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      
      {/* 1. Organization Dropdown */}
      <div className="w-full">
        <label className="block text-sm font-medium text-slate-700 mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>
          GitHub Organization / Owner
        </label>
        <div className="relative">
          <select
            value={selectedOrg}
            onChange={handleOrgChange}
            disabled={loadingOrgs || orgs.length === 0}
            className="w-full appearance-none bg-white border border-slate-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent text-slate-800 disabled:bg-slate-100 disabled:text-slate-500 transition-shadow duration-200"
          >
            <option value="">
              {loadingOrgs ? 'Loading organizations...' : orgs.length === 0 ? 'No organizations found' : 'Select an organization...'}
            </option>
            {orgs.map((org) => (
              <option key={org} value={org}>
                {org}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* 2. Repository Dropdown */}
      <div className="w-full relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-slate-700 mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>
          Repository
        </label>
        <button
          type="button"
          onClick={() => selectedOrg && setIsOpen(!isOpen)}
          disabled={!selectedOrg}
          className="w-full flex items-center justify-between bg-white border border-slate-300 rounded-lg px-4 py-2 text-left focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent transition-shadow duration-200 disabled:bg-slate-100 disabled:text-slate-500"
        >
          <span className={`truncate font-medium ${!selectedOrg ? 'text-slate-400' : 'text-slate-700'}`}>
            {selectedRepo ? selectedRepo.name : (selectedOrg ? 'Select a repository...' : 'Select an organization first')}
          </span>
          <ChevronDown className={`w-5 h-5 ${!selectedOrg ? 'text-slate-300' : 'text-slate-400'}`} />
        </button>

        {isOpen && selectedOrg && (
          <div className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden flex flex-col">
            <div className="p-3 border-b border-slate-100 bg-slate-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search repositories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e] transition-shadow duration-200 text-slate-800"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              {loadingRepos ? (
                <div className="p-4 text-center text-slate-500 text-sm">Loading repositories...</div>
              ) : repos.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">No repos found</div>
              ) : (
                <ul className="py-1">
                  {repos.map((repo) => (
                    <li key={repo.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectRepo(repo)}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none transition-colors group"
                      >
                        <div className="flex items-center space-x-2">
                          {repo.private ? (
                            <Lock className="w-3.5 h-3.5 text-[#ff7a59] flex-shrink-0" />
                          ) : (
                            <Globe className="w-3.5 h-3.5 text-[#1a237e] flex-shrink-0" />
                          )}
                          <span className="font-medium text-slate-800 text-sm truncate group-hover:text-[#1a237e] transition-colors" style={{ fontFamily: 'Sora, sans-serif' }}>
                            {repo.name}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 pl-5.5 flex items-center space-x-2">
                          <span>Updated {new Date(repo.updated_at).toLocaleDateString()}</span>
                          {repo.default_branch && (
                            <>
                              <span>•</span>
                              <span>{repo.default_branch}</span>
                            </>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default GitHubOrgRepoDropdown;
